import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/shared/FormField';
import { toast } from '@/lib/swal';
import { cloneMaster, type EmailTemplateMaster } from '../api/platform-email-template.api';

interface Props {
  open: boolean;
  template: EmailTemplateMaster;
  onClose: () => void;
  onCloned: () => void;
}

const KEY_RE = /^[a-z0-9]+(?:[.-][a-z0-9]+)*$/;

/** Clone dialog (COMMUNICATION_DESIGN.md §6). The clone is always created as DRAFT. */
export function CloneDialog({ open, template, onClose, onCloned }: Props) {
  const [templateKey, setTemplateKey] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTemplateKey(`${template.templateKey}-copy`);
      setName(`${template.name} (Copy)`);
      setError(null);
    }
  }, [open, template]);

  const keyValid = KEY_RE.test(templateKey.trim());
  const canSubmit = keyValid && name.trim().length > 0 && !busy;

  const handleClone = async () => {
    setError(null);
    setBusy(true);
    try {
      await cloneMaster(template.id, templateKey.trim(), name.trim());
      toast.success('Template cloned as draft');
      onCloned();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clone failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Clone ${template.name}`} size="md">
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Creates a new draft template with the same content. The original is untouched.
        </p>

        <FormField label="New template key" htmlFor="clone-template-key" required>
          <Input
            id="clone-template-key"
            value={templateKey}
            onChange={(e) => setTemplateKey(e.target.value)}
            disabled={busy}
            className="font-mono text-xs"
          />
          {templateKey.trim() && !keyValid && (
            <p className="mt-1 text-[11px] text-destructive">
              Lowercase letters, digits, dots and hyphens only (e.g. auth.welcome-v2).
            </p>
          )}
        </FormField>

        <FormField label="Name" htmlFor="clone-template-name" required>
          <Input
            id="clone-template-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
        </FormField>

        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void handleClone()} disabled={!canSubmit}>
            {busy && <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />}
            Clone
          </Button>
        </div>
      </div>
    </Modal>
  );
}
